// @flow
import ResourceModel from "../base/ResourceModel";
import TaskGroupCollection from "../taskgroup/TaskGroupCollection";

import TaskGroupModel from "../taskgroup/TaskGroupModel";

import type { ModularUIResponse } from "../../modularui";
import type { ModularUIModel } from "../types";
import type LinkModel from "../links/LinkModel";
import type LinkCollection from "../links/LinkCollection";

/**
 * Panel that groups other panels
 */
export default class GroupingPanelModel extends ResourceModel {
  _taskGroupCollection: TaskGroupCollection;

  /**
   * Constructs GroupingPanel
   */
  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    this.createTaskGroupCollection();
  }

  /**
   */
  get type(): string {
    return "GroupingPanel";
  }

  /**
   */
  static get modelName(): string {
    return "GroupingPanelModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      ["GroupingPanel", "CaseTabGroupingPanel", "TabGroupingPanel"].includes(
        data.contributions.resourcetype,
      )
    );
  }

  /**
   * Getting the label of the panel
   */
  get label(): string {
    return this.contributions.label;
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this.links.getLinksByGroup("taskgroup").all;
  }

  /**
   */
  get panelLinks(): LinkCollection {
    return this.links.getLinksByGroup("panel");
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>) {
    const taskGroups = [];

    for (let i = 0; i < models.length; i++) {
      if (models[i] instanceof TaskGroupModel) {
        taskGroups.push(models[i]);
      }
    }

    this.taskGroupCollection.add(taskGroups);
  }

  /**
   * Check if an introtext exists for this caseview
   */
  hasIntroText(): boolean {
    return this.introtext !== "";
  }

  /**
   * Getting the introduction text configured on the grouping panel
   */
  get introtext(): string {
    if (this.data._content) {
      return this.data._content.text.message;
    }

    if (this.contributions.text) {
      return this.contributions.text.message ?? this.contributions.text;
    }

    if (Array.isArray(this.contributions.texts)) {
      return this.contributions.texts[0].text;
    }

    return "";
  }

  /**
   */
  createTaskGroupCollection() {
    const allTaskgroupsData = this.getData("taskgroups", []);
    const allTaskgroupsContributions = this.getContribution("taskgroups", []);

    this.taskGroupCollection = allTaskgroupsData
      .filter((taskgroup) =>
        allTaskgroupsContributions.some(
          (taskgroupContribution) =>
            taskgroupContribution.name === taskgroup.name,
        ),
      )
      .map((taskgroup) => {
        const taskgroupContributions = allTaskgroupsContributions.find(
          (taskgroupContribution) =>
            taskgroupContribution.name === taskgroup.name,
        );

        return TaskGroupModel.create(
          taskgroup.name,
          taskgroup,
          taskgroupContributions,
          this.modelOptions,
        );
      });
  }

  /**
   * Setting the taskgroup panel collection
   */
  set taskGroupCollection(taskgroups: Array<TaskGroupModel>) {
    this._taskGroupCollection = new TaskGroupCollection(taskgroups);
  }

  /**
   * Getting the taskgrouppanels on the tab
   */
  get taskGroupCollection(): TaskGroupCollection {
    return this._taskGroupCollection;
  }

  /**
   * Has taskgroups
   */
  hasTaskGroups(): boolean {
    return this.taskGroupCollection && this.taskGroupCollection.hasItems;
  }

  /**
   * Has tasks
   */
  hasTasks(): boolean {
    return this.taskGroupCollection.hasTasks();
  }
}
